import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toastFlash } from '@/lib/flash-toast';
import type { SponsorHackerOption } from '@/types';

interface CreateSponsorCreditDialogProps {
  sponsorId: string;
  sponsorSlug: string;
  eventSlug: string;
  hackerOptions: SponsorHackerOption[];
}

export function CreateSponsorCreditDialog({
  sponsorId,
  sponsorSlug,
  eventSlug,
  hackerOptions,
}: CreateSponsorCreditDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    sponsorId,
    hackerProfileId: '',
    url: '',
    notes: '',
  });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/sponsors/${sponsorSlug}/credits`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Credit created successfully!');
        reset();
        setOpen(false);
      },
    });
  };

  const comboboxOptions = hackerOptions.map((h) => ({
    value: h.hackerProfileId,
    label: `${h.hackerName} (${h.hackerEmail})`,
  }));

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Credit
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Credit</DialogTitle>
            <DialogDescription>
              Assign a credit from this sponsor to a hacker.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-credit-hacker">Hacker</Label>
              <Combobox
                options={comboboxOptions}
                value={data.hackerProfileId}
                onValueChange={(value) => setData('hackerProfileId', value)}
                placeholder="Select a hacker..."
                searchPlaceholder="Search by name or email..."
                emptyText="No hackers found."
                disabled={processing}
              />
              {errors.hackerProfileId && (
                <p className="text-destructive text-sm">
                  {errors.hackerProfileId}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-credit-url">URL (Optional)</Label>
              <Input
                id="create-sponsor-credit-url"
                placeholder="https://..."
                disabled={processing}
                value={data.url}
                onChange={(e) => setData('url', e.target.value)}
              />
              {errors.url && (
                <p className="text-destructive text-sm">{errors.url}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-credit-notes">
                Notes (Optional)
              </Label>
              <Textarea
                id="create-sponsor-credit-notes"
                placeholder="Free-form notes..."
                disabled={processing}
                rows={4}
                value={data.notes}
                onChange={(e) => setData('notes', e.target.value)}
              />
              {errors.notes && (
                <p className="text-destructive text-sm">{errors.notes}</p>
              )}
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
